import { TriangleAlert } from "lucide-react";

import type { AuditEvent, Fact, FactKey } from "../api";
import {
  eventSentenceParts,
  exactDate,
  humanTitle,
  marketLabel,
  productLabel,
  timeOfDay,
  valueWithUnit,
} from "../format";

/**
 * One audit event, as the sentence a person would actually say.
 *
 * The tab's whole question is "what changed" — a row of columns
 * (`approve · damien · {n:899}`) makes the reader reassemble that themselves
 * every time. The product is named because a nutrition number without one is
 * unreadable, and OMITTED rather than guessed when the fact behind the event has
 * not resolved: a sentence naming the wrong product is worse than one naming
 * none.
 *
 * The approved mock-up sets this as a 52px time gutter beside a sentence block,
 * with NO rule between events: the day headings are what divide the feed, and a
 * border under every row made four events read as a four-row table. Events are
 * separated by the list's own spacing instead.
 */
export function AuditEventSentence({
  event,
  fact,
  factKey,
  onOpenFact,
}: {
  event: AuditEvent;
  /** The fact the event belongs to, when it has resolved. */
  fact: Fact | undefined;
  factKey: FactKey | undefined;
  onOpenFact?: (factId: string) => void;
}) {
  // The fact's name from the REGISTRY (KET-6326), the same source as the Audit
  // tab's key filter. Not the fact read's: that lands after first paint, and
  // the title would change under the reader once it did.
  const title = humanTitle(event.fact_key, factKey);
  // The scope only when it NARROWS the fact. "All markets" is the default every
  // unscoped fact carries, and prefixing it to every meta line made the one
  // scope worth reading — "UK · Amazon" — look like more of the same. The
  // mock-up prints a scope only where there is one.
  const scope = fact ? marketLabel(fact.scope) : null;
  const market = scope && scope !== "All markets" ? scope : null;
  const unit = fact?.unit ?? factKey?.unit ?? null;
  const parts = eventSentenceParts({
    action: event.action,
    actor: event.actor,
    title,
    product: fact ? productLabel(fact) : null,
    value: fact ? valueWithUnit(fact.value, unit) : null,
    priorValue: event.prior_value ? valueWithUnit(event.prior_value, unit) : null,
  });

  return (
    <li className="flex gap-[14px]">
      <time
        dateTime={event.created_at}
        title={exactDate(event.created_at)}
        className="w-[52px] shrink-0 text-[13px] leading-[1.5] text-ks-grey-1 tabular-nums"
      >
        {timeOfDay(event.created_at)}
      </time>
      <div className="min-w-0 space-y-[3px]">
        <p className="text-sm leading-[1.5] text-ks-ink">
          {/* Actor, fact and product carry the weight — they are what a reader
              scans a trail for — and "(was …)" is grey context for the value,
              as the mock-up sets it. */}
          <strong className="font-bold">{parts.actor}</strong> {parts.verb}{" "}
          <strong className="font-bold">{parts.title}</strong>
          {parts.product && (
            <>
              {" on "}
              <strong className="font-bold">{parts.product}</strong>
            </>
          )}
          {parts.value && (
            <>
              {" — "}
              {/* A number never wraps away from its unit ("15 / ml"). */}
              <span className="whitespace-nowrap">{parts.value}</span>
              {parts.was && (
                <span className={parts.replaced ? undefined : "text-ks-grey-1"}> {parts.was}</span>
              )}
            </>
          )}
          {/* Self-approval proceeds and is LABELLED, never blocked — blocking it
              would strand a single-author domain unable to publish anything. It
              sits INSIDE the sentence, as the mock-up draws it: on its own line
              underneath, the badge read as a second, separate claim about the
              event rather than as a qualifier on the one just made. */}
          {event.self_approved && (
            <span className="ml-1 inline-flex h-5 items-center rounded-full bg-ks-grey-4 px-[7px] text-[11px] text-ks-ink">
              Self-approved
            </span>
          )}
        </p>
        {event.source_locator ? (
          <p className="text-[13px] leading-[1.5] text-ks-grey-1">
            {market && <span>{market} · </span>}
            Checked against: {event.source_locator}
          </p>
        ) : (
          <>
            {/* The scope keeps its own quiet line here rather than prefixing the
                finding below — "UK · Amazon · No source recorded" buries the one
                sentence on this row that is meant to stop the eye. */}
            {market && <p className="text-[13px] leading-[1.5] text-ks-grey-1">{market}</p>}
            {/* The defect class the whole system exists to end, so it reads as a
                FINDING — marked, not a blank cell nobody's eye stops on. */}
            <p className="flex items-center gap-1.5 text-[13px] leading-[1.5]">
              <TriangleAlert aria-hidden className="size-3.5 shrink-0 text-ks-danger" />
              <span className="text-ks-danger">No source recorded</span>
            </p>
          </>
        )}
        {onOpenFact && (
          <button
            type="button"
            data-nonmutating
            className="block text-[13px] text-ks-purple underline-offset-2 hover:underline"
            onClick={() => onOpenFact(event.fact_id)}
          >
            Open this fact
          </button>
        )}
      </div>
    </li>
  );
}
